import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  apiURL: string = environment.apiURL;
  fileType: string;

  /**
   * Emits when quality control reports are uploaded from the work order header
   */
  qualityControlReportsChanged = new Subject<void>();

  constructor(private http: HttpClient) {}

  /**
   * Get orders
   */
  getOrders(data: {skip: number, limit: number, link: string, assigneeId?: string, sector?: string, search?: string, jobType?: string, productionDeadline?: string, deliveryDeadline?: string, requestedDate?: string, operationStatus?: string}): Observable<any> {
    let params = new HttpParams()
      .set('skip', data.skip)
      .set('limit', data.limit);

    if (data.sector) params = params.append('sector', data.sector);
    if (data.assigneeId) params = params.append('assigneeId', data.assigneeId);
    if (data.search) params = params.append('search', data.search);
    if (data.jobType) params = params.append('jobType', data.jobType);
    if (data.productionDeadline) params = params.append('productionDeadline', data.productionDeadline);
    if (data.deliveryDeadline) params = params.append('deliveryDeadline', data.deliveryDeadline);
    if (data.operationStatus) params = params.append('operationStatus', data.operationStatus);
    if (data.requestedDate) params = params.append('requestedDate', data.requestedDate);

    return this.http.get(`${this.apiURL}/work-orders/${data.link}`, { params });
  }

  getDevelopmentWorkOrders(link: string, assigneeId?: string, requestedDate?: any, search?: string): Observable<any> {
    let params = new HttpParams();
  
    if (link) params = params.append('link', link);
    if (assigneeId) params = params.append('assigneeId', assigneeId);
    if (requestedDate) params = params.append('requestedDate', requestedDate);
    if (search) params = params.append('search', search);
    
    return this.http.get(`${this.apiURL}/work-orders/development`, {params});
  }

  /**
   * Get order details
   */
  getOrderDetails(orderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${orderId}`);
  }

  /**
   * Confirm Upis update
   */
  confirmUpisUpdate(orderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/upis-confirm`, {});
  }

  /**
   * Create order
   */
  createOrder(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/work-orders`, data);
  }

  /**
   * Edit order
   */
  editOrder(data: any, orderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}`, data);
  }

  /**
   * Add order sector
   */
  addOrderSector(sector: any, orderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/sectors`, sector);
  }

  /**
   * Get order documents
   */
  getOrderDocuments(orderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${orderId}/documents`);
  }

  /**
   * Add document
   */
  addDocument(data: FormData, orderId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/work-orders/${orderId}/documents`, data);
  }

  /**
   * Edit document
   */
  editDocument(data: any, orderId: string, documentId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/documents/${documentId}`, data);
  }

  /**
   * Delete document
   */
  deleteDocument(orderId: string, documentId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/work-orders/${orderId}/documents/${documentId}`);
  }

  /**
   * Approve document
   */
  approveDocument(orderId: string, documentId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/documents/${documentId}/approve`, {});
  }

  /**
   * Download document
   */
  downloadDocument(documentId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/documents/${documentId}/single-download`);
  }

  /**
   * Download all documents
   */
  downloadAllDocuments(orderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${orderId}/documents/download`, {
      responseType: 'blob',
      observe: 'body'
    });
  }

  /**
   * Change order status
   */
  changeOrderStatus(orderId: string, status: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/status`, { status });
  }

  /**
   * Sync work orders from Upis
   */
  onSyncWorkOrders(): Observable<any> {
    return this.http.post(`${this.apiURL}/work-orders/sync`, {});
  }

  /**
   * Change category status
   */
  changeCategoryStatus(orderId: string, type: string, action: string, status: string, sector?: string): Observable<any> {
    const data = {
      status: status,
      sector: '',
    };

    if (sector) {
      data.sector = sector;
    } else {
      delete data.sector;
    }

    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/${type}-list/${action}`, data);
  }

  /**
   * Add material
   */
  addMaterial(data: any, orderId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/work-orders/${orderId}/materials`, data);
  }

  /**
   * Edit material
   */
  editMaterial(data: any, materialId: string, orderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/materials/${materialId}`, data);
  }

  /**
   * Approve material
   */
  approveMaterial(orderId: string, materialId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/materials/${materialId}/approve`, {});
  }

  /**
   * Delete material
   */
  deleteMaterial(orderId: string, materialId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/work-orders/${orderId}/materials/${materialId}`);
  }

  /**
   * Import materials from order
   */
  importMaterialsFromOrder(orderId: string, fromOrderId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/work-orders/${orderId}/materials/import/${fromOrderId}`, {});
  }

  /**
   * Get order operations
   */
  getOrderOperations(orderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${orderId}/operations`);
  }

  /**
   * Add operation
   */
  addOperation(data: any, orderId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/work-orders/${orderId}/operations`, data);
  }

  /**
   * Edit operation
   */
  editOperation(data: any, operationId: string, orderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/operations/${operationId}`, data);
  }

  /**
   * Operations order
   */
  operationsOrder(data: any, orderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/operations`, data);
  }

  /**
   * Delete operation
   */
  deleteOperation(orderId: string, operationId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/work-orders/${orderId}/operations/${operationId}`);
  }

  /**
   * Import operations from order
   */
  importOperationsFromOrder(orderId: string, orderForImportId: string, data?: any): Observable<any> {
    return this.http.post(`${this.apiURL}/work-orders/${orderId}/operations/import/${orderForImportId}`, data);
  }

  /**
   * Get order cover page
   */
  getOrderCoverPage(orderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${orderId}/cover-page`);
  }

  /**
   * Submit order cover page
   */
  submitOrderCoverPage(orderId: string, coverPageProperties): Observable<any> {
    const data = {
      coverPageProperties: coverPageProperties,
    };
    return this.http.post(`${this.apiURL}/work-orders/${orderId}/cover-page`, data);
  }

  /**
   * Download order cover page
   */
  downloadCoverPage(orderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${orderId}/cover-page/download`, {
      responseType: 'blob',
      observe: 'body',
    });
  }

  /**
   * Get order assignees
   */
  getOrderAssignees(orderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${orderId}/assignees`);
  }

  /**
   * Get order customer service assignees
   */
  getCustomerServiceWorkOrderAssignees(workOrderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${workOrderId}/assignees/customer-service`);
  }

  /**
   * Assign order
   */
  assignOrder(orderId: string, assigneeId: string, comment: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/assignees/${assigneeId}`, comment);
  }

  /**
   * Get operation stats
   */
  getOperationsStats(orderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${orderId}/operations/stat`);
  }

  /**
   * Get operation stats
   */
  getWorkSheetsStats(skip: number, limit: number, orderId: string): Observable<any> {
    let params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);
      
    return this.http.get(`${this.apiURL}/work-orders/${orderId}/worksheets/stat`, { params });
  }

  /**
   * Add material consumption
   */
  addMaterialConsumption(orderId: string, materialId: string, data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/materials/${materialId}/consumed`, data);
  }

  /**
   * Work order packing properties
   */
  savePackingProperties(data: any, orderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/packing`, data);
  }

  /**
   * Archive work order
   */
  archiveOrder(orderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/archive`, {});
  }

  /**
   * Delete work order
   */
  deleteOrder(orderId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/work-orders/${orderId}`);
  }

  /**
   * Get order deadlines
   */
  getOrderDeadlines(orderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${orderId}/partial-delivery-deadline`);
  }

  /**
   * Get deadline details
   */
  getDeadlineDetails(orderId: string, deadlineId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${orderId}/partial-delivery-deadline/${deadlineId}`);
  }

  /**
   * Create deadline
   */
  createDeadline(data: any, orderId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/work-orders/${orderId}/partial-delivery-deadline`, data);
  }

  /**
   * Edit deadline
   */
  editDeadline(data: any, orderId: string, deadlineId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/partial-delivery-deadline/${deadlineId}`, data);
  }

  /**
   * Delete order deadline
   */
  deleteDeadline(orderId: string, deadlineId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/work-orders/${orderId}/partial-delivery-deadline/${deadlineId}`);
  }

  /**
   * Change order priority status
   */
  changeOrderPriorityStatus(orderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/set-priority`, {});
  }

  /**
   * Change status for engineers and graphic artists
   */
  changeConfirmationStatusEngineersAndGraphic(orderId: string, status: boolean, statusLabel: string): Observable<any> {
    let params = new HttpParams();

    params = params.append(statusLabel, status);

    return this.http.patch(`${this.apiURL}/work-orders/${orderId}/set-completion`, null, { params });
  }

  /**
   * Import product to work order
   */
  importProductToWorkOrder(workOrderId: string, productId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/work-orders/${workOrderId}/products/${productId}`, {});
  }
  
  /**
   * Download list of materials by sector
   */
  downloadMaterials(orderId: string, sector: string): Observable<any> {
    let params = new HttpParams();

    if (sector) params = params.append('sector', sector);

    return this.http.get(`${this.apiURL}/work-orders/${orderId}/materials/requisition/download`, {
      params: params,
      responseType: 'blob',
      observe: 'body'
    }).pipe(
      tap(
        res => {
          this.fileType = res.type;
        }
      )
    );
  }

  /**
   * Get work order operations by product
   */
  getWorkOrderOperationsByProduct(workOrderId: string, sector: string): Observable<any> {
    let params = new HttpParams();

    if (sector) params = params.append('sector', sector);

    return this.http.get(`${this.apiURL}/work-orders/${workOrderId}/product/operations`, {params});
  }

  /**
   * Unlock work order
   */
  unlockWorkOrder(workOrderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${workOrderId}/unlock-documents`, {});
  }

  /**
   * Remove product from work order
   */
  removeProductFromWorkOrder(workOrderId: string, productId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${workOrderId}/products/${productId}/remove`, {});
  }

  /**
   * Download work orders by sector
   */
  downloadWorkOrdersBySector(workOrderId: string, sector: string): Observable<any> {
    let params = new HttpParams();

    if (sector) params = params.append('sector', sector);
    
    return this.http.get(`${this.apiURL}/work-orders/${workOrderId}/download`, {
      params: params,
      responseType: 'blob',
      observe: 'body'
    });
  }

  /**
   * Add additional product to work order
   */
  addAdditionalProductToWorkOrder(workOrderId: string, productId: string, data: {quantity: number}): Observable<any> {
    return this.http.post(`${this.apiURL}/work-orders/${workOrderId}/products/${productId}/additional`, data);
  }

  /**
   * Get work order operations sector status activity logs
   */
  getWorkOrderOperationsActivityLogs(workOrderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${workOrderId}/operations-list/approve-logs`);
  }

  /**
   * Get work order materials activity logs
   */
  getWorkOrderMaterialsActivityLogs(workOrderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${workOrderId}/materials/activities`);
  }

  /**
   * Download materials specification
   */
  downloadMaterialsSpecification(workOrderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${workOrderId}/materials/download-specifications`, { responseType: 'blob', observe: 'body' });
  }

  /**
   * Get materials specification lock status
   */
  getMaterialsSpecificationLockStatus(workOrderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${workOrderId}/materials/specification/status`);
  }

  /**
   * Lock materials specification
   */
  lockMaterialsSpecification(workOrderId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/work-orders/${workOrderId}/materials/specification/lock`, {});
  }

  /**
   * Unlock materials specification
   */
  unlockMaterialsSpecification(workOrderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${workOrderId}/materials/specification/unlock`, {});
  }

  /**
   * Remove sector duplicates
   */
  removeSectorDuplicates(workOrderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${workOrderId}/remove-duplicated-sectors`, {});
  }

  /**
   * Download quality control report
   */
  downloadQualityControlReport(workOrderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${workOrderId}/quality-control-report`, { responseType: 'blob', observe: 'body' });
  }

  /**
   * Get list of uploaded quality control reports
   */
  getQualityControlReports(workOrderId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/${workOrderId}/quality-control-reports`);
  }

  /**
   * Upload filled quality control reports
   */
  addQualityControlReports(data: FormData, workOrderId: string): Observable<any> {
    return this.http.post(`${this.apiURL}/work-orders/${workOrderId}/quality-control-reports`, data);
  }

  /**
   * Get temporary signed link for an uploaded quality control report
   */
  downloadUploadedQualityControlReport(reportId: number): Observable<any> {
    return this.http.get(`${this.apiURL}/work-orders/quality-control-reports/${reportId}/download`);
  }

  /**
   * Delete uploaded quality control report
   */
  deleteQualityControlReport(workOrderId: string, reportId: number): Observable<any> {
    return this.http.delete(`${this.apiURL}/work-orders/${workOrderId}/quality-control-reports/${reportId}`);
  }

  /**
   * Set work order for delivery
   */
  setWorkOrderForDelivery(workOrderId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${workOrderId}/set-for-delivery`, {});
  }

  /**
   * Set work order complexity level
   */
  setWorkOrderComplexityLevel(workOrderId: string, complexity: number): Observable<any> {
    return this.http.patch(`${this.apiURL}/work-orders/${workOrderId}/complexity`, { complexity });
  }

  /**
   * Get list of development work order operations
   * @param search
   */
  getDevelopmentJobOperations(): Observable<any> {
    return this.http.get(`${this.apiURL}/development-tasks/name`);
  }

  /**
   * Get development work order details
   * @param orderId
   */
  getListOfTasksForDevelopmentEmployees(id: string, type: 'order' | 'request', date: string): Observable<any> {
    let params = new HttpParams();
    
     if (type === 'order') {
      params = params.set('workOrderId', id.toString());
    } else if (type === 'request') {
      params = params.set('workRequestId', id.toString());
    }
    if (date) params = params.append('date', date);

    return this.http.get(`${this.apiURL}/development-tasks/users`, { params });
  }

  /**
   * Create work order development task
   */
  createDevelopmentTask(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/development-tasks`, data);
  }

  /**
   * Get work order development tasks
   */
  getDevelopmentJobTasks(id: string, type: 'order' | 'request'): Observable<any> {
    let params = new HttpParams();

    if (type === 'order') {
      params = params.set('workOrderId', id.toString());
    } else if (type === 'request') {
      params = params.set('workRequestId', id.toString());
    }

    return this.http.get(`${this.apiURL}/development-tasks`, { params });
  }

  /**
   * Update work order development task
   */
  updateDevelopmentTask(developmentTaskId: string, data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/development-tasks/${developmentTaskId}`, data);
  }

  /**
   * Delete work order development task
   */
  deleteDevelopmentTask(developmentTaskId: string): Observable<any> {
    return this.http.delete(`${this.apiURL}/development-tasks/${developmentTaskId}`);
  }

  /**
   * Download employees report from development sector for period
   */
  downloadDevelopmentEmployeeReport(fromDate: string, toDate: string): Observable<any> {
    let params = new HttpParams();

    if (fromDate) params = params.append('fromDate', fromDate);
    if (toDate) params = params.append('toDate', toDate);

    return this.http.get(`${this.apiURL}/development-tasks/download-report`, { 
      params: params, 
      responseType: 'blob', 
      observe: 'body' 
    });
  }

}
